'use client';
import React from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { DollarSign, Clock, Star } from 'lucide-react';
import { QuoteFormData } from './QuickQuoteWizard';

const budgetOptions = [
  { id: 'budget-under500', value: 'Under $500', label: 'Under $500', sub: 'Small repairs or touch-ups' },
  { id: 'budget-500-2k', value: '$500–$2,000', label: '$500–$2,000', sub: 'Mid-size single room job' },
  { id: 'budget-2k-5k', value: '$2,000–$5,000', label: '$2,000–$5,000', sub: 'Multi-room or full floor' },
  { id: 'budget-5k-10k', value: '$5,000–$10,000', label: '$5,000–$10,000', sub: 'Large project or whole home' },
  { id: 'budget-10kplus', value: '$10,000+', label: '$10,000+', sub: 'Commercial or major renovation' },
];

const timelineOptions = [
  { id: 'tl-week', value: 'Within 1 week', label: 'Within 1 week', urgency: 'high' },
  { id: 'tl-2week', value: 'Within 2 weeks', label: 'Within 2 weeks', urgency: 'high' },
  { id: 'tl-month', value: 'Within a month', label: 'Within a month', urgency: 'medium' },
  { id: 'tl-flexible', value: 'Flexible / Just exploring', label: 'Flexible / Just exploring', urgency: 'low' },
];

const priorityOptions = [
  { id: 'pri-must', value: 'Must-have — I need this done', label: 'Must-have', sub: 'Non-negotiable, moving forward regardless' },
  { id: 'pri-nice', value: 'Nice-to-have — depends on price', label: 'Nice-to-have', sub: 'Will proceed if the price is right' },
  { id: 'pri-exploring', value: 'Just exploring options', label: 'Just exploring', sub: 'No commitment yet, gathering info' },
];

interface Step2Props {
  form: UseFormReturn<QuoteFormData>;
  onNext: () => void;
  onBack: () => void;
}

export default function Step2LeverageQuestions({ form, onNext, onBack }: Step2Props) {
  const { setValue, watch } = form;
  const budgetRange = watch('budgetRange');
  const timeline = watch('timeline');
  const priority = watch('priority');

  const canProceed = !!budgetRange && !!timeline && !!priority;

  return (
    <div className="space-y-7">
      <div>
        <h2 className="text-2xl font-800 text-white mb-1">The Leverage Questions</h2>
        <p className="text-[#888] text-sm">3 quick answers help us match you with the right solution — fast.</p>
      </div>

      {/* Q1: Budget */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <DollarSign size={13} className="text-amber-400" />
          </div>
          <label className="text-white font-600 text-sm">What&apos;s your budget range?</label>
        </div>
        <div className="space-y-2">
          {budgetOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setValue('budgetRange', opt.value)}
              className={`
                w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-all duration-150
                ${budgetRange === opt.value
                  ? 'bg-amber-500/10 border-amber-500/40 text-white' :'bg-[#0D0D0D] border-[#2A2A2A] text-[#888] hover:border-[#3A3A3A] hover:text-[#CCC]'
                }
              `}
            >
              <div>
                <span className={`text-sm font-600 ${budgetRange === opt.value ? 'text-amber-300' : ''}`}>{opt.label}</span>
                <p className="text-[11px] text-[#555] mt-0.5">{opt.sub}</p>
              </div>
              {budgetRange === opt.value && (
                <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-black" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Q2: Timeline */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
            <Clock size={13} className="text-blue-400" />
          </div>
          <label className="text-white font-600 text-sm">When do you need this completed?</label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {timelineOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setValue('timeline', opt.value)}
              className={`
                px-3 py-3 rounded-lg border text-left text-sm font-500 transition-all duration-150
                ${timeline === opt.value
                  ? opt.urgency === 'high' ?'bg-blue-600/20 border-blue-600/50 text-blue-300'
                    : opt.urgency === 'medium' ?'bg-blue-600/10 border-blue-600/30 text-blue-400' :'bg-[#1A1A1A] border-[#3A3A3A] text-[#AAA]' :'bg-[#0D0D0D] border-[#2A2A2A] text-[#888] hover:border-[#3A3A3A] hover:text-white'
                }
              `}
            >
              {opt.label}
              {opt.urgency === 'high' && timeline === opt.value && (
                <span className="block text-[10px] text-blue-400 mt-0.5 font-600">URGENT</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Q3: Priority */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Star size={13} className="text-purple-400" />
          </div>
          <label className="text-white font-600 text-sm">Is this a must-have or nice-to-have?</label>
        </div>
        <div className="space-y-2">
          {priorityOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setValue('priority', opt.value)}
              className={`
                w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-all duration-150
                ${priority === opt.value
                  ? 'bg-purple-500/10 border-purple-500/40 text-white' :'bg-[#0D0D0D] border-[#2A2A2A] text-[#888] hover:border-[#3A3A3A] hover:text-[#CCC]'
                }
              `}
            >
              <div>
                <span className={`text-sm font-600 ${priority === opt.value ? 'text-purple-300' : ''}`}>{opt.label}</span>
                <p className="text-[11px] text-[#555] mt-0.5">{opt.sub}</p>
              </div>
              {priority === opt.value && (
                <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-black" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button type="button" onClick={onBack} className="leverage-btn-secondary flex-shrink-0 px-5 py-4">
          ← Back
        </button>
        <button type="button" onClick={onNext} disabled={!canProceed} className="leverage-btn-primary flex-1 py-4 text-base">
          Final Step →
        </button>
      </div>
    </div>
  );
}